// Component Implementation
import React, { useState, useEffect, useMemo } from 'react';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Edit,
  Save,
  Cancel,
  FileDownload,
  FilterList,
} from '@mui/icons-material';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Checkbox,
  CircularProgress,
  Box,
  Typography,
  IconButton,
  Button,
  Collapse,
} from '@mui/material';
import OrgActionMenu, { MenuItemType } from '@/components/molecules/ActionMenu';
import dataMuiTableStyles from './datamuitable.module.css';

// Types
interface Column {
  id: string;
  label: string;
  minWidth?: number;
  maxWidth?: number;
  width?: number;
  align?: 'left' | 'right' | 'center';
  format?: (value: any) => string | React.ReactNode;
  sortable?: boolean;
  filterable?: boolean;
  filterOptions?: string[];
  editable?: boolean;
  validate?: (value: any) => boolean | string;
}

interface DataTableProps {
  columns: Column[];
  rows: any[];
  loading?: boolean;
  getRowId?: (row: any) => string | number;
  containerClassName?: string;
  dense?: boolean;
  stickyHeader?: boolean;
  title?: string;
  searchable?: boolean;
  selectable?: boolean;
  pagination?: boolean;
  collapsible?: boolean;
  editable?: boolean;
  filterable?: boolean;
  exportable?: boolean;
  onRowClick?: (row: any) => void;
  onSelectionChange?: (selectedRows: any[]) => void;
  onSearchChange?: (searchTerm: string) => void;
  onRowUpdate?: (newRow: any, oldRow: any) => Promise<any>;
  renderDetail?: (row: any) => React.ReactNode;
  toolbar?: React.ReactNode;
  actions?: MenuItemType[];
  singleExpand?: boolean;
  onExpandChange?: (rowId: string | number, isExpanded: boolean, row: any) => void;
}

export const DataTable: React.FC<DataTableProps> = ({
  columns,
  rows,
  loading = false,
  getRowId = row => row.id,
  title,
  searchable = false,
  selectable = false,
  pagination = true,
  collapsible = false,
  editable = false,
  filterable = false,
  exportable = false,
  onRowClick,
  onSelectionChange,
  onSearchChange,
  onRowUpdate,
  renderDetail,
  toolbar,
  actions,
  singleExpand = false,
  onExpandChange,
}) => {
  // States
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [editingRow, setEditingRow] = useState<any | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [sortConfig, setSortConfig] = useState<{ field: string; order: 'asc' | 'desc' }>({
    field: '',
    order: 'asc',
  });
  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(new Set());

  // Data Processing
  const processedRows = useMemo(() => {
    let result = [...rows];
    if (searchTerm) {
      result = result.filter(row =>
        Object.values(row).some(value =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
    if (sortConfig.field) {
      result.sort((a, b) => {
        const aValue = a[sortConfig.field];
        const bValue = b[sortConfig.field];
        if (aValue === bValue) return 0;
        const compareResult = aValue < bValue ? -1 : 1;
        return sortConfig.order === 'asc' ? compareResult : -compareResult;
      });
    }
    return result;
  }, [rows, searchTerm, sortConfig]);

  const paginatedRows = useMemo(() => {
    if (!pagination) return processedRows;
    const startIndex = page * rowsPerPage;
    return processedRows.slice(startIndex, startIndex + rowsPerPage);
  }, [processedRows, page, rowsPerPage, pagination]);

  // Event Handlers
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
    onSearchChange?.(event.target.value);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedRows(paginatedRows);
      onSelectionChange?.(paginatedRows);
    } else {
      setSelectedRows([]);
      onSelectionChange?.([]);
    }
  };

  const handleSelectRow = (row: any) => {
    const selectedIndex = selectedRows.findIndex(r => getRowId(r) === getRowId(row));
    const newSelected = [...selectedRows];

    if (selectedIndex === -1) {
      newSelected.push(row);
    } else {
      newSelected.splice(selectedIndex, 1);
    }

    setSelectedRows(newSelected);
    onSelectionChange?.(newSelected);
  };

  const handleSort = (columnId: string) => {
    const isAsc = sortConfig.field === columnId && sortConfig.order === 'asc';
    setSortConfig({
      field: columnId,
      order: isAsc ? 'desc' : 'asc',
    });
  };

  // Updated handleExpandRow to include the row data
  const handleExpandRow = (event: React.MouseEvent, rowId: string | number, row: any) => {
    event.stopPropagation();
    setExpandedRows(prev => {
      if (singleExpand) {
        // Single expand mode - only one row at a time
        const next = new Set<string | number>();
        if (!prev.has(rowId)) {
          next.add(rowId);
          onExpandChange?.(rowId, true, row);
        } else {
          onExpandChange?.(rowId, false, row);
        }
        return next;
      } else {
        // Multiple expand mode - original behavior
        const next = new Set(prev);
        const willExpand = !next.has(rowId);

        if (next.has(rowId)) {
          next.delete(rowId);
        } else {
          next.add(rowId);
        }

        onExpandChange?.(rowId, willExpand, row);
        return next;
      }
    });
  };

  const handleStartEdit = (row: any) => {
    setEditingRow({ ...row });
    setValidationErrors({});
  };

  const handleEditChange = (field: string, value: any) => {
    setEditingRow(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveEdit = async (oldRow: any) => {
    try {
      await onRowUpdate?.(editingRow, oldRow);
      setEditingRow(null);
      setValidationErrors({});
    } catch (error) {
      console.error('Failed to update row:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setValidationErrors({});
  };

  const getColSpan = () => {
    let span = columns.length;
    if (selectable) span += 1;
    if (collapsible) span += 1;
    if (editable || actions) span += 1; // Modified this line
    return span;
  };

  // JSX
  return (
    <div className={dataMuiTableStyles.gridContainer}>
      {/* Header Section */}
      <div className={dataMuiTableStyles.headerSection}>
        <div className={dataMuiTableStyles.header}>
          {title && <h3>{title}</h3>}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {exportable && (
              <Button startIcon={<FileDownload />} size="small" variant="outlined">
                Export
              </Button>
            )}
          </Box>
        </div>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {searchable && (
            <TextField
              size="small"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearch}
              sx={{ minWidth: 300 }}
            />
          )}
          {toolbar}
        </Box>
      </div>

      {/* Table Section */}
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {collapsible && (
                <TableCell
                  className={dataMuiTableStyles.coloumnHeader}
                  padding="checkbox"
                  style={{ width: 42 }}
                />
              )}

              {selectable && (
                <TableCell padding="checkbox" className={dataMuiTableStyles.coloumnHeader}>
                  <Checkbox
                    indeterminate={
                      selectedRows.length > 0 && selectedRows.length < paginatedRows.length
                    }
                    checked={
                      paginatedRows.length > 0 && selectedRows.length === paginatedRows.length
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
              )}

              {columns.map(column => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                  className={dataMuiTableStyles.coloumnHeader}
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={sortConfig.field === column.id}
                      direction={sortConfig.field === column.id ? sortConfig.order : 'asc'}
                      onClick={() => handleSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}

              {(editable || actions) && (
                // Action cell just removed name for empty header
                <TableCell className={dataMuiTableStyles.coloumnHeader} align="center"></TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={getColSpan()} align="center" sx={{ py: 3 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : paginatedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={getColSpan()} align="center" sx={{ py: 3 }}>
                  No data available
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map(row => (
                <React.Fragment key={getRowId(row)}>
                  <TableRow hover onClick={() => !editingRow && onRowClick?.(row)}>
                    {collapsible && (
                      <TableCell padding="checkbox">
                        <IconButton
                          size="small"
                          onClick={e => handleExpandRow(e, getRowId(row), row)}
                        >
                          {expandedRows.has(getRowId(row)) ? (
                            <KeyboardArrowUp />
                          ) : (
                            <KeyboardArrowDown />
                          )}
                        </IconButton>
                      </TableCell>
                    )}

                    {selectable && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedRows.some(r => getRowId(r) === getRowId(row))}
                          onChange={() => handleSelectRow(row)}
                          onClick={e => e.stopPropagation()}
                        />
                      </TableCell>
                    )}

                    {columns.map(column => (
                      <TableCell key={column.id} align={column.align}>
                        {editingRow?.id === getRowId(row) && column.editable ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={editingRow[column.id] || ''}
                            onChange={e => handleEditChange(column.id, e.target.value)}
                            error={!!validationErrors[column.id]}
                            helperText={validationErrors[column.id]}
                            onClick={e => e.stopPropagation()}
                          />
                        ) : column.format ? (
                          column.format(row[column.id])
                        ) : (
                          row[column.id]
                        )}
                      </TableCell>
                    ))}

                    {editable && (
                      <TableCell align="center">
                        {editingRow?.id === getRowId(row) ? (
                          <>
                            <IconButton
                              color="primary"
                              onClick={() => handleSaveEdit(row)}
                              size="small"
                            >
                              <Save fontSize="small" />
                            </IconButton>
                            <IconButton color="error" onClick={handleCancelEdit} size="small">
                              <Cancel fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <IconButton onClick={() => handleStartEdit(row)} size="small">
                            <Edit fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    )}

                    {actions && !editable && (
                      <TableCell align="center">
                        <TableCell className={dataMuiTableStyles.actionCell}>
                          <OrgActionMenu menuItems={actions} rowData={row} rowId={row.id} />
                        </TableCell>
                      </TableCell>
                    )}
                  </TableRow>

                  {collapsible && renderDetail && (
                    <TableRow>
                      <TableCell colSpan={getColSpan()} style={{ paddingBottom: 0, paddingTop: 0 }}>
                        <Collapse in={expandedRows.has(getRowId(row))} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 1 }}>{renderDetail(row)}</Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {pagination && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={processedRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={event => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      )}
    </div>
  );
};
